import React, { useMemo, useState } from 'react';
import {
  ColumnPicker, DataTable, DateRangePicker, DensityToggle, EmptyState, exportFilename, exportRows,
  FilterBar, formatThaiDate, SearchInput, SideDrawer, StatusBadge, Toolbar, useTableDensity, useToast,
  type ActiveFilter, type Column, type DateRange, type SortState,
  resolveLink, type NavLinkComponent,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  CLAIMS, SLA_HOURS, SLA_METRICS, SLA_WARN_HOURS, STAFF, pointsFor,
  type Channel, type Claim, type Staff,
} from './mockData';

/**
 * SlaMonitorScreen — ตัวชี้วัดคิวงาน (FR-ADM-14)
 *
 * หน้านี้ตอบคำถามเดียวของหัวหน้าทีม: "วันนี้เราจะทันคำสัญญา 1–3 วันทำการหรือไม่"
 * ทุกตัวเลขจึงชี้ไปที่การกระทำ ไม่ใช่แค่รายงาน — กดตัวเลขได้ทุกตัวเพื่อเปิดรายการที่อยู่ข้างหลัง
 *
 * SLA ที่แจ้งลูกค้าคือ 1–3 วันทำการ (72 ชม.) เตือนที่ 48 ชม.
 * เมื่อเชื่อม API ของแพลตฟอร์มสำเร็จ ตัวเลข "ระบบตรวจอัตโนมัติ" จะโตขึ้นและคิวคนจะเล็กลง
 * — สัดส่วนสองเส้นนี้คือตัวชี้ว่าการลงทุนเชื่อม API ได้ผลจริงหรือไม่
 */

type ScreenState = 'ready' | 'loading' | 'error';
type Drill = 'overdue' | 'near-due' | 'backlog' | null;

const CHANNELS: Channel[] = ['Shopee', 'Lazada', 'TikTok Shop', 'ร้านขายยา', 'บูธ', 'อื่น ๆ'];

/* -------------------------------------------------------------- การ์ดตัวเลข */

const MetricCard: React.FC<{
  label: string;
  value: string;
  unit?: string;
  hint?: string;
  tone?: 'neutral' | 'warn' | 'over' | 'good';
  icon: string;
  onClick?: () => void;
}> = ({ label, value, unit, hint, tone = 'neutral', icon, onClick }) => {
  const skin = {
    neutral: 'border-[var(--line-200)] bg-[var(--surface-0)] text-[var(--ink-900)]',
    good: 'border-[var(--success-border)] bg-[var(--success-bg)] text-[var(--success-text)]',
    warn: 'border-[var(--warning-border)] bg-[var(--warning-bg)] text-[var(--warning-text)]',
    over: 'border-[var(--error-border)] bg-[var(--error-bg)] text-[var(--error-text)]',
  }[tone];

  const inner = (
    <>
      <span className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] leading-[var(--leading-form)]">
        <Icon name={icon} size={18} filled />
        {label}
      </span>
      <span className="bo-num text-[length:var(--text-metric)] font-[var(--weight-bold)] leading-[var(--leading-num)]" style={{ textAlign: 'left' }}>
        {value}
        {unit && <span className="ml-1 text-[length:var(--text-form)] font-[var(--weight-regular)]">{unit}</span>}
      </span>
      {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] opacity-90">{hint}</span>}
    </>
  );

  const base = `flex min-w-[210px] flex-1 flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border p-[var(--space-3)] text-left ${skin}`;

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${base} hover:brightness-[0.98] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2`}
    >
      {inner}
      <span className="mt-[var(--space-05)] inline-flex items-center gap-1 text-[length:var(--text-table-sm)] font-[var(--weight-medium)] underline underline-offset-2">
        ดูรายการ
        <Icon name="arrow_forward" size={14} />
      </span>
    </button>
  ) : (
    <div className={base}>{inner}</div>
  );
};

/* ------------------------------------------------ กราฟแท่งปริมาณงานรายวัน */

const DailyChart: React.FC = () => {
  const max = Math.max(...SLA_METRICS.daily.flatMap((d) => [d.slip + d.orderId, d.cleared]));
  return (
    <section aria-label="ปริมาณงานเข้าคิวและงานที่เคลียร์ได้ 14 วันล่าสุด" className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)]">
      <div className="mb-[var(--space-3)] flex flex-wrap items-baseline gap-[var(--space-3)]">
        <h3 className="text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
          งานเข้าคิวเทียบกับงานที่เคลียร์ได้ — 14 วันล่าสุด
        </h3>
        <span className="flex flex-wrap items-center gap-[var(--space-3)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
          <span className="flex items-center gap-1"><span aria-hidden className="h-3 w-3 rounded-[2px] bg-[var(--blue-700)]" />สลิป (ต้องใช้คน)</span>
          <span className="flex items-center gap-1"><span aria-hidden className="h-3 w-3 rounded-[2px] bg-[var(--blue-500)]" />Order ID ที่ต้องใช้คน</span>
          <span className="flex items-center gap-1"><span aria-hidden className="h-3 w-[14px] border-b-2 border-dashed border-[var(--success-solid)]" />เคลียร์ได้</span>
        </span>
      </div>

      <div className="flex h-[168px] items-end gap-[var(--space-2)]" role="presentation">
        {SLA_METRICS.daily.map((d) => {
          const inbound = d.slip + d.orderId;
          const behind = d.cleared < inbound;
          return (
            <div key={d.date} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-[var(--space-1)]">
              <div className="relative flex w-full flex-col justify-end" style={{ height: 132 }}>
                <div
                  aria-hidden
                  className="w-full rounded-t-[3px] bg-[var(--blue-500)]"
                  style={{ height: `${(d.orderId / max) * 132}px` }}
                />
                <div
                  aria-hidden
                  className="w-full bg-[var(--blue-700)]"
                  style={{ height: `${(d.slip / max) * 132}px` }}
                />
                {/* เส้นงานที่เคลียร์ได้ — ถ้าอยู่ต่ำกว่ายอดแท่ง วันนั้นงานค้างเพิ่ม */}
                <span
                  aria-hidden
                  className="absolute left-0 w-full border-b-2 border-dashed border-[var(--success-solid)]"
                  style={{ bottom: `${(d.cleared / max) * 132}px` }}
                />
              </div>
              <span className="bo-num text-[length:var(--text-micro)] leading-[1.2] text-[var(--ink-600)]" style={{ textAlign: 'center' }}>
                {d.date.slice(-2)}
              </span>
              {behind && <Icon name="trending_up" size={12} label={`วันที่ ${d.date} งานค้างเพิ่ม`} className="text-[var(--warning-text)]" />}
            </div>
          );
        })}
      </div>

      {/* ตารางข้อมูลใต้กราฟ — เป็นทางเข้าถึงสำหรับผู้ใช้ screen reader และให้อ่านค่าตรง ๆ ได้ */}
      <details className="mt-[var(--space-3)]">
        <summary className="cursor-pointer text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2">
          ดูเป็นตาราง
        </summary>
        <table className="mt-[var(--space-2)] w-full text-[length:var(--text-table)] leading-[var(--leading-table)]">
          <caption className="sr-only">ปริมาณงานเข้าคิวและงานที่เคลียร์ได้รายวัน</caption>
          <thead className="bg-[var(--surface-100)]">
            <tr>
              <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-left text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">วันที่</th>
              <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">สลิป</th>
              <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">Order ID</th>
              <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">เคลียร์ได้</th>
              <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">คงค้างเพิ่ม</th>
            </tr>
          </thead>
          <tbody>
            {SLA_METRICS.daily.map((d) => {
              const delta = d.slip + d.orderId - d.cleared;
              return (
                <tr key={d.date} className="border-t border-[var(--line-200)]">
                  <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]" style={{ textAlign: 'left' }}>{d.date}</td>
                  <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]">{d.slip}</td>
                  <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]">{d.orderId}</td>
                  <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]">{d.cleared}</td>
                  <td className={`bo-num px-[var(--space-2)] py-[var(--space-1)] ${delta > 0 ? 'text-[var(--warning-text)]' : 'text-[var(--success-text)]'}`}>
                    {delta > 0 ? `+${delta}` : delta}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </details>
    </section>
  );
};

/* ------------------------------------------------------ ปริมาณงานรายเจ้าหน้าที่ */

const StaffTable: React.FC<{ onPick: (s: Staff) => void }> = ({ onPick }) => {
  const maxReviewed = Math.max(...STAFF.map((s) => s.reviewedToday));
  return (
    <section aria-label="ปริมาณงานรายเจ้าหน้าที่" className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)]">
      <h3 className="mb-[var(--space-3)] text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
        ปริมาณงานรายเจ้าหน้าที่ — วันนี้
      </h3>
      <table className="w-full text-[length:var(--text-table)] leading-[var(--leading-table)]">
        <caption className="sr-only">จำนวนที่ตรวจเสร็จ เวลาเฉลี่ยต่อรายการ และงานที่ถืออยู่ ของเจ้าหน้าที่แต่ละคน</caption>
        <thead className="bg-[var(--surface-100)]">
          <tr>
            <th scope="col" className="px-[var(--space-2)] py-[var(--space-15)] text-left text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">เจ้าหน้าที่</th>
            <th scope="col" className="px-[var(--space-2)] py-[var(--space-15)] text-left text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ตรวจเสร็จวันนี้</th>
            <th scope="col" className="px-[var(--space-2)] py-[var(--space-15)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">เวลาเฉลี่ย/รายการ</th>
            <th scope="col" className="px-[var(--space-2)] py-[var(--space-15)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">งานที่ถืออยู่</th>
            <th scope="col" className="px-[var(--space-2)] py-[var(--space-15)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]" />
          </tr>
        </thead>
        <tbody>
          {STAFF.map((s) => (
            <tr key={s.id} className="border-t border-[var(--line-200)] hover:bg-[var(--row-bg-hover)]">
              <td className="px-[var(--space-2)] py-[var(--space-15)]">
                <span className="font-[var(--weight-medium)] text-[var(--ink-900)]">{s.name}</span>
                <span className="block text-[length:var(--text-table-sm)] text-[var(--ink-600)]">{s.role}</span>
              </td>
              <td className="px-[var(--space-2)] py-[var(--space-15)]">
                <span className="flex items-center gap-[var(--space-2)]">
                  <span className="bo-num w-[42px] font-[var(--weight-semibold)]" style={{ textAlign: 'left' }}>{s.reviewedToday}</span>
                  <span aria-hidden className="h-[8px] flex-1 overflow-hidden rounded-[var(--radius-pill)] bg-[var(--surface-100)]">
                    <span className="block h-full rounded-[var(--radius-pill)] bg-[var(--blue-700)]" style={{ width: `${(s.reviewedToday / maxReviewed) * 100}%` }} />
                  </span>
                </span>
              </td>
              <td className="bo-num px-[var(--space-2)] py-[var(--space-15)]">
                {s.avgSeconds} วินาที
                {s.avgSeconds > 60 && <span className="ml-1 text-[var(--warning-text)]">ช้ากว่าค่ากลาง</span>}
              </td>
              <td className="bo-num px-[var(--space-2)] py-[var(--space-15)]">{s.assigned}</td>
              <td className="px-[var(--space-2)] py-[var(--space-15)] text-right">
                <button
                  type="button"
                  onClick={() => onPick(s)}
                  className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 hover:text-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                >
                  ดูคิวของคนนี้
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-[var(--space-2)] text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
        ตัวเลขนี้ใช้ดูว่างานกระจายสมดุลหรือไม่ ไม่ใช่การจัดอันดับคน — คำขอที่มีสิ่งผิดปกติใช้เวลาตรวจนานกว่าปกติหลายเท่า
      </p>
    </section>
  );
};

/* ------------------------------------------------------------------- หน้าจอ */

export interface SlaMonitorScreenProps {
  /** host เป็นคนตัดสินว่ากดลิงก์แล้วไปไหน — ดู components/NavLink */
  linkComponent?: NavLinkComponent;
  initialState?: ScreenState;
}

export const SlaMonitorScreen: React.FC<SlaMonitorScreenProps> = ({ initialState = 'ready', linkComponent }) => {
  const Link = resolveLink(linkComponent);
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [drill, setDrill] = useState<Drill>(null);
  const [staffDrill, setStaffDrill] = useState<Staff | null>(null);
  const [density, setDensity] = useTableDensity();
  const [query, setQuery] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [sort, setSort] = useState<SortState | null>({ columnId: 'age', direction: 'desc' });
  const [visibleCols, setVisibleCols] = useState<string[]>([]);

  const open = useMemo(
    () => CLAIMS.filter((c) => c.status === 'pending' || c.status === 'review' || c.status === 'overdue'),
    [],
  );

  /** อัตราทันกำหนด = งานที่ยังไม่เกิน SLA / งานค้างทั้งหมด */
  const onTimeRate = open.length ? Math.round(((open.length - SLA_METRICS.overdue) / open.length) * 100) : 100;

  /** ชั่วโมงที่ต้องใช้เคลียร์งานค้างด้วยกำลังคนวันนี้ */
  const capacityPerHour = STAFF.reduce((s, st) => s + 3600 / st.avgSeconds, 0);
  const hoursToClear = capacityPerHour ? (open.length / capacityPerHour).toFixed(1) : '—';

  const drilled = useMemo(() => {
    let pool = open;
    if (drill === 'overdue') pool = pool.filter((c) => c.ageHours > SLA_HOURS);
    if (drill === 'near-due') pool = pool.filter((c) => c.ageHours >= SLA_WARN_HOURS && c.ageHours <= SLA_HOURS);
    if (staffDrill) pool = pool.filter((c) => c.assigneeId === staffDrill.id);
    return pool.filter((c) => {
      if (channels.length && !channels.includes(c.channel)) return false;
      if (range.from && c.submittedAt.slice(0, 10) < range.from) return false;
      if (range.to && c.submittedAt.slice(0, 10) > range.to) return false;
      if (query && !c.code.includes(query.trim()) && !c.member.name.includes(query.trim())) return false;
      return true;
    });
  }, [open, drill, staffDrill, channels, range, query]);

  const sorted = useMemo(() => {
    if (!sort) return drilled;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (c: Claim): string | number =>
      sort.columnId === 'age' ? c.ageHours : sort.columnId === 'amount' ? c.declaredAmount : c.code;
    return [...drilled].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [drilled, sort]);

  const columns: Array<Column<Claim>> = useMemo(() => [
    { id: 'code', header: 'รหัสคำขอ', width: 122, pinned: true, required: true, sortable: true, value: (c) => c.code },
    { id: 'member', header: 'สมาชิก', width: 170, value: (c) => c.member.name },
    { id: 'channel', header: 'ช่องทาง', width: 116, value: (c) => c.channel },
    {
      id: 'age', header: 'อายุคำขอ', width: 150, sortable: true, numeric: true,
      value: (c) => c.ageHours,
      cell: (c) => {
        const over = c.ageHours > SLA_HOURS;
        const near = !over && c.ageHours >= SLA_WARN_HOURS;
        return (
          <span className={`inline-flex items-center gap-1 ${over ? 'text-[var(--error-text)]' : near ? 'text-[var(--warning-text)]' : 'text-[var(--ink-900)]'}`}>
            {(over || near) && <Icon name={over ? 'running_with_errors' : 'schedule'} size={14} filled />}
            <span className="bo-num">{Math.floor(c.ageHours / 24)} วัน {c.ageHours % 24} ชม.</span>
          </span>
        );
      },
    },
    { id: 'status', header: 'สถานะ', width: 150, value: (c) => c.status, cell: (c) => <StatusBadge status={c.status} /> },
    { id: 'amount', header: 'ยอด (บาท)', width: 118, numeric: true, sortable: true, value: (c) => c.declaredAmount, cell: (c) => c.declaredAmount.toLocaleString('th-TH') },
    { id: 'points', header: 'คะแนน', width: 96, numeric: true, value: (c) => pointsFor(c.declaredAmount), cell: (c) => pointsFor(c.declaredAmount).toLocaleString('th-TH') },
    { id: 'assignee', header: 'ผู้รับผิดชอบ', width: 150, value: (c) => STAFF.find((s) => s.id === c.assigneeId)?.name ?? 'ยังไม่มอบหมาย' },
    { id: 'submittedAt', header: 'วันที่ส่ง', width: 118, value: (c) => formatThaiDate(c.submittedAt.slice(0, 10)) },
  ], []);

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (drill) out.push({ id: 'drill', field: 'กลุ่มงาน', display: drill === 'overdue' ? 'เกิน SLA แล้ว' : drill === 'near-due' ? 'ใกล้เกิน SLA' : 'งานค้างทั้งหมด' });
    if (staffDrill) out.push({ id: 'staff', field: 'ผู้รับผิดชอบ', display: staffDrill.name });
    if (channels.length) out.push({ id: 'channel', field: 'ช่องทาง', display: channels.join(', ') });
    if (range.from || range.to) out.push({ id: 'range', field: 'ช่วงวันที่', display: `${formatThaiDate(range.from)} – ${formatThaiDate(range.to)}` });
    return out;
  }, [drill, staffDrill, channels, range]);

  const clearAll = () => { setDrill(null); setStaffDrill(null); setChannels([]); setRange({ from: null, to: null }); setQuery(''); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: visible, filename: exportFilename('รายงาน SLA คิวงาน'), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว — บันทึกใน Audit Log` });
  };

  const listOpen = drill !== null || staffDrill !== null;

  return (
    <div className="flex min-h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--blue-050)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="SLA และงานเกินกำหนด"
        end={
          <>
            <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
              คำสัญญาที่แจ้งลูกค้า: ตรวจภายใน 1–3 วันทำการ · เตือนที่ {SLA_WARN_HOURS} ชม.
            </span>
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      {screenState === 'error' ? (
        <div role="alert" className="m-[var(--space-6)] flex flex-col items-center gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--error-border)] bg-[var(--error-bg)] p-[var(--space-10)] text-center">
          <Icon name="error" size={32} filled className="text-[var(--error-text)]" />
          <p className="text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--error-text)]">
            โหลดตัวชี้วัดไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์รายงานไม่ได้
          </p>
          <button
            type="button"
            onClick={() => setScreenState('ready')}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-white outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            ลองใหม่
          </button>
        </div>
      ) : screenState === 'loading' ? (
        <div className="flex flex-col gap-[var(--space-4)] p-[var(--space-6)]" aria-busy="true">
          <p aria-live="polite" className="sr-only">กำลังโหลดตัวชี้วัด</p>
          <div className="flex flex-wrap gap-[var(--space-3)]">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="min-w-[210px] flex-1 rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
                <div className="h-[16px] w-[60%] animate-pulse rounded-[var(--radius-xs)] bg-[var(--surface-100)]" />
                <div className="mt-[var(--space-2)] h-[30px] w-[40%] animate-pulse rounded-[var(--radius-xs)] bg-[var(--surface-100)]" />
              </div>
            ))}
          </div>
          <div className="h-[240px] animate-pulse rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)]" />
          <div className="h-[220px] animate-pulse rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)]" />
        </div>
      ) : open.length === 0 ? (
        <EmptyState
          kind="queue-clear"
          title="ไม่มีงานค้างในคิว"
          description="ทุกคำขอถูกตัดสินแล้ว — ไม่มีรายการที่เสี่ยงเกิน SLA"
          className="m-[var(--space-8)]"
        />
      ) : (
        <div className="flex flex-col gap-[var(--space-4)] p-[var(--space-6)]">
          <div className="flex flex-wrap gap-[var(--space-3)]">
            <MetricCard
              icon="running_with_errors"
              label="เกิน SLA แล้ว"
              value={SLA_METRICS.overdue.toLocaleString('th-TH')}
              unit="รายการ"
              hint="ลูกค้ารอเกิน 3 วันทำการ — ต้องเคลียร์ก่อนงานอื่น"
              tone={SLA_METRICS.overdue > 0 ? 'over' : 'good'}
              onClick={() => { setDrill('overdue'); setStaffDrill(null); }}
            />
            <MetricCard
              icon="schedule"
              label="ใกล้เกิน SLA"
              value={SLA_METRICS.nearDue.toLocaleString('th-TH')}
              unit="รายการ"
              hint={`ค้างเกิน ${SLA_WARN_HOURS} ชม. เหลือเวลาไม่ถึง 1 วันทำการ`}
              tone={SLA_METRICS.nearDue > 0 ? 'warn' : 'good'}
              onClick={() => { setDrill('near-due'); setStaffDrill(null); }}
            />
            <MetricCard
              icon="inbox"
              label="งานค้างทั้งหมด"
              value={open.length.toLocaleString('th-TH')}
              unit="รายการ"
              hint={`ด้วยกำลังคนวันนี้ใช้เวลาราว ${hoursToClear} ชม. จึงเคลียร์หมด`}
              onClick={() => { setDrill('backlog'); setStaffDrill(null); }}
            />
            <MetricCard
              icon="timer"
              label="เวลาเฉลี่ยในการตรวจ"
              value={SLA_METRICS.avgReviewSeconds.toString()}
              unit="วินาที/รายการ"
              hint="นับจากเปิดรายการถึงกดตัดสิน"
            />
            <MetricCard
              icon="verified"
              label="อัตราทันกำหนด"
              value={`${onTimeRate}`}
              unit="%"
              hint="ของงานค้างที่ยังอยู่ในกรอบ 3 วันทำการ"
              tone={onTimeRate >= 95 ? 'good' : onTimeRate >= 85 ? 'warn' : 'over'}
            />
          </div>

          <div className="flex flex-wrap gap-[var(--space-3)]">
            <MetricCard icon="check_circle" label="อนุมัติวันนี้ (คน)" value={SLA_METRICS.approvedToday.toLocaleString('th-TH')} unit="รายการ" tone="good" />
            <MetricCard icon="cancel" label="ปฏิเสธวันนี้" value={SLA_METRICS.rejectedToday.toLocaleString('th-TH')} unit="รายการ" />
            <MetricCard
              icon="bolt"
              label="ระบบตรวจอัตโนมัติวันนี้"
              value={SLA_METRICS.autoApprovedToday.toLocaleString('th-TH')}
              unit="รายการ"
              hint="ไม่ผ่านคิวคน — ตัวเลขนี้ยิ่งโต คิวคนยิ่งเล็กลง"
              tone="good"
            />
          </div>

          <DailyChart />
          <StaffTable onPick={(s) => { setStaffDrill(s); setDrill(null); }} />
        </div>
      )}

      {/* รายการที่อยู่ข้างหลังตัวเลข — เปิดจากการกดการ์ดหรือชื่อเจ้าหน้าที่ */}
      <SideDrawer
        open={listOpen}
        onClose={clearAll}
        width="lg"
        title={staffDrill ? `คิวของ ${staffDrill.name}` : drill === 'overdue' ? 'รายการที่เกิน SLA แล้ว' : drill === 'near-due' ? 'รายการที่ใกล้เกิน SLA' : 'งานค้างทั้งหมด'}
        subtitle={`${sorted.length.toLocaleString('th-TH')} รายการ`}
      >
        <div className="flex flex-col gap-[var(--space-3)]">
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <SearchInput value={query} onChange={setQuery} placeholder="ค้นหารหัสคำขอหรือชื่อสมาชิก" width={240} />
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="sla-monitor" value={visible} onChange={setVisibleCols} />
          </div>

          <FilterBar
            active={activeFilters}
            onRemove={(id) => {
              if (id === 'drill') setDrill(null);
              if (id === 'staff') setStaffDrill(null);
              if (id === 'channel') setChannels([]);
              if (id === 'range') setRange({ from: null, to: null });
            }}
            onClearAll={clearAll}
            currentFilters={{ drill, staff: staffDrill?.id, channels, range }}
            persistKey="sla-monitor"
            resultCount={sorted.length}
            className="rounded-[var(--radius-sm)] border border-[var(--line-200)] px-[var(--space-2)]"
          >
            <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
              ช่องทาง
              <select
                multiple
                size={1}
                value={channels}
                onChange={(e) => setChannels([...e.target.selectedOptions].map((o) => o.value as Channel))}
                className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <DateRangePicker value={range} onChange={setRange} />
          </FilterBar>

          <div className="h-[520px]">
            <DataTable<Claim>
              columns={columns.filter((c) => visible.includes(c.id))}
              rows={sorted}
              rowKey={(c) => c.id}
              caption="รายการงานค้างตามตัวชี้วัดที่เลือก"
              emptyState={<EmptyState kind="no-results" title="ไม่พบรายการในกลุ่มนี้" action={{ label: 'ล้างตัวกรอง', onClick: clearAll }} />}
              sort={sort}
              onSortChange={setSort}
              density={density}
              persistKey="sla-monitor"
              isFlagged={(c) => c.ageHours > SLA_HOURS}
              className="h-full"
            />
          </div>

          <Link
            to="/queue/claims"
            className="inline-flex min-h-[var(--hit-primary)] w-fit items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-white no-underline hover:bg-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            <Icon name="gavel" size={18} />
            เปิดคิวเพื่อเริ่มตรวจรายการเหล่านี้
          </Link>
        </div>
      </SideDrawer>
    </div>
  );
};

export default SlaMonitorScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-14  ตัวชี้วัดคิวงาน — งานค้าง · เวลาเฉลี่ยในการตรวจ · งานที่เกิน SLA ·
              ปริมาณงานรายเจ้าหน้าที่ · อัตราทันกำหนด · เวลาที่ต้องใช้เคลียร์งานค้าง
   FR-ADM-10  รายการที่อยู่ข้างหลังทุกตัวเลข เปิดดูได้จากการกดการ์ด
   FR-ADM-11  แยกปริมาณงานที่ระบบตรวจอัตโนมัติออกจากคิวคน เพื่อวัดผลการเชื่อม API
   FR-RPT-09  ข้อมูลชุดเดียวกับรายงานการอัปโหลดใบเสร็จ (สัดส่วนอนุมัติ/ปฏิเสธรายวัน)
   FR-ADM-39  การส่งออกถูกบันทึกและแจ้งผู้ใช้
   ประเด็นค้าง: ข้อ ค — เมื่อเชื่อม API สำเร็จ คำสัญญา SLA ที่แจ้งลูกค้าจะเปลี่ยน
   ต้องยืนยันว่า 1–3 วันทำการยังใช้กับเส้นทางสลิปต่อไปหรือไม่
   ข้อ ฏ — ตัวเลข "งานที่ถืออยู่" สมมติว่ามีการมอบหมายงาน (assignee) ซึ่ง SRS บทที่ 5
   ยังไม่มี FR รองรับ
   ============================================================================ */
